'use client';

import useDebounce from '@ui/lib/hooks/useDebounce';
import { ChangeEvent, useCallback, useState } from 'react';

export type ColumnConfig = {
  key: string;
  header?: string;
  width?: string;
  className?: string;
  hideOnMobile?: boolean;
  stackOnMobile?: boolean;
};

export type FilterableOption = {
  key: string | number;
  columns: {
    [key: string]: React.ReactNode;
  };
};

interface FilterableRadioButtonsProps {
  options: FilterableOption[];
  columns: ColumnConfig[];
  initialValueKey?: string | number;
  onChange: (value: FilterableOption) => void;
  onFilterChange?: (filterText: string) => void;
  placeholder?: string;
  className?: string;
  filterClassName?: string;
  isSelectable?: boolean;
  variant?: 'default' | 'horizontal';
  loading?: boolean;
  filterBy?: string; // Column key to filter by
}

export function FilterableRadioButtons({
  options,
  columns,
  initialValueKey,
  onChange,
  onFilterChange,
  placeholder = 'Search...',
  className,
  filterClassName,
  isSelectable = true,
  variant = 'default',
  loading = false,
  filterBy,
}: FilterableRadioButtonsProps) {
  const [filterText, setFilterText] = useState('');
  const debouncedFilterText = useDebounce(filterText, 300);

  const handleFilterChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFilterText(value);
      if (onFilterChange) {
        onFilterChange(value);
      }
    },
    [onFilterChange]
  );

  const filteredOptions = options.filter((option) => {
    if (!filterText) return true;
    if (!filterBy) return true;
    const content = option.columns[filterBy];
    if (typeof content === 'string') {
      return content.toLowerCase().includes(filterText.toLowerCase());
    }
    return true;
  });

  return (
    <div className={className}>
      <input
        type="text"
        value={filterText}
        onChange={handleFilterChange}
        placeholder={placeholder}
        className={`block w-full px-3 py-2 text-sm border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-black focus:border-slate-300 outline-none mb-2 ${filterClassName}`}
      />

      {loading ? (
        <div className="animate-pulse space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 bg-slate-200 rounded-lg w-full"></div>
          ))}
        </div>
      ) : (
        <div className="w-full">
          {/* Headers */}
          <div
            className="hidden md:grid gap-4 px-3 py-2 text-slate-700"
            style={{
              gridTemplateColumns: columns
                .map((col) => col.width || '1fr')
                .join(' '),
            }}
          >
            {columns.map((column) => (
              <div
                key={column.key}
                className={`text-sm font-bold text-ellipsis ${column.className}`}
              >
                {column.header}
              </div>
            ))}
          </div>

          {/* Options */}
          <div className="space-y-2">
            {filteredOptions.map((option) => (
              <div
                key={option.key}
                className={`border rounded-lg cursor-pointer transition-all
                  ${
                    option.key === initialValueKey
                      ? 'bg-green-100 ring-green-800 ring-2'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                onClick={() => isSelectable && onChange(option)}
              >
                {/* Desktop view */}
                <div
                  className="hidden md:grid gap-4 px-3 py-3"
                  style={{
                    gridTemplateColumns: columns
                      .map((col) => col.width || '1fr')
                      .join(' '),
                  }}
                >
                  {columns.map((column) => (
                    <div
                      key={column.key}
                      className={`flex items-center text-sm text-ellipsis ${column.className}`}
                    >
                      {option.columns[column.key]}
                    </div>
                  ))}
                </div>

                {/* Mobile view */}
                <div className="md:hidden p-3 space-y-1">
                  <div className="flex items-center gap-3">
                    {columns
                      .filter((col) => !col.hideOnMobile && !col.stackOnMobile)
                      .map((column) => (
                        <div
                          key={column.key}
                          className={`flex items-center text-sm ${column.className}`}
                        >
                          {option.columns[column.key]}
                        </div>
                      ))}
                  </div>
                  {columns
                    .filter((col) => col.stackOnMobile)
                    .map((column) => (
                      <div key={column.key} className="text-xs text-slate-600">
                        {column.header}:{' '}
                        <span className="text-slate-800">
                          {option.columns[column.key]}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// For backward compatibility
export default FilterableRadioButtons;
